import { fetchConfig, onAnimationEnd, parseIntOrDefault, preloadImage, yieldToMainThread } from "@theme/utilities";
import { Component } from "@theme/component";
import { cartPerformance } from "@theme/performance";
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from "@shopify/events";
import { morph } from "@theme/morph";
import { resolveVariantId } from "@theme/variant-resolution";
import { buildPdpUpsellCartItems, cartAttributesFromFormData, collectSelectedUpsellVariantIds } from "@theme/vp-pdp-upsell";
//#region assets/product-availability.ts
/** Refreshes each product availability surface from server-rendered variant HTML. */
function refreshProductAvailability(currentRoot, nextRoot, update) {
	const nextTargets = /* @__PURE__ */ new Map();
	for (const nextElement of nextRoot.querySelectorAll("[data-product-availability]")) {
		const surface = nextElement.dataset.productAvailability;
		if (surface) nextTargets.set(surface, nextElement);
	}
	for (const currentElement of currentRoot.querySelectorAll("[data-product-availability]")) {
		const surface = currentElement.dataset.productAvailability;
		if (!surface) continue;
		const nextElement = nextTargets.get(surface);
		if (nextElement) update(currentElement, nextElement);
	}
}
/**
* Resolves the product-form subtree from a section render response.
* Falls back to the full document when no matching form is found.
*/
function getProductFormRoot(html, productId) {
	if (productId) {
		const scoped = html.querySelector(`product-form-component[data-product-id="${productId}"]`);
		if (scoped) return scoped;
	}
	return html;
}
/**
* Aligns availability UI with the resolved variant JSON when section HTML is stale.
* Sold-out selections are forced; available selections only fix a leftover unavailable state
* so delivery-promise labels from morph are preserved.
*/
function syncProductAvailabilityFromVariant(root, variant) {
	if (!variant || typeof variant.available !== "boolean") return;
	for (const element of root.querySelectorAll("[data-product-availability]")) {
		const dot = element.querySelector(".vp-product-availability__dot");
		if (!dot) continue;
		if (variant.available === false) {
			dot.classList.remove("vp-product-availability__dot--available", "vp-product-availability__dot--unavailable", "vp-product-availability__dot--promise");
			dot.style.removeProperty("--vp-product-availability-color");
			dot.classList.add("vp-product-availability__dot--unavailable");
			const outOfStockLabel = element.dataset.outOfStockLabel?.trim();
			if (outOfStockLabel) setAvailabilityLabel(element, outOfStockLabel);
			continue;
		}
		if (!dot.classList.contains("vp-product-availability__dot--unavailable")) continue;
		dot.classList.remove("vp-product-availability__dot--unavailable", "vp-product-availability__dot--promise");
		dot.style.removeProperty("--vp-product-availability-color");
		dot.classList.add("vp-product-availability__dot--available");
		const inStockLabel = element.dataset.inStockLabel?.trim();
		if (inStockLabel) setAvailabilityLabel(element, inStockLabel);
	}
}
function setAvailabilityLabel(element, label) {
	for (const node of [...element.childNodes]) if (node.nodeType === Node.TEXT_NODE) node.remove();
	element.append(document.createTextNode(` ${label}`));
}
//#endregion
//#region assets/product-form.ts
var ERROR_MESSAGE_DISPLAY_DURATION = 1e4;
var ERROR_BUTTON_REENABLE_DELAY = 1e3;
var SUCCESS_MESSAGE_DISPLAY_DURATION = 5e3;
/**
* @typedef {HTMLElement & {
*   source: Element,
*   destination: Element,
*   useSourceSize: string | boolean
* }} FlyToCart
*/
/**
* A custom element that manages an add to cart button.
*
* @typedef {object} AddToCartRefs
* @property {HTMLButtonElement} addToCartButton - The add to cart button.
* @extends Component<AddToCartRefs>
*/
var AddToCartComponent = class extends Component {
	requiredRefs = ["addToCartButton"];
	/** @type {number[] | undefined} */
	#resetTimeouts = [];
	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("pointerenter", this.#preloadImage);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.#resetTimeouts) this.#resetTimeouts.forEach(
			/** @param {number} timeoutId */
			(timeoutId) => clearTimeout(timeoutId)
		);
		this.removeEventListener("pointerenter", this.#preloadImage);
	}
	/**
	* Disables the add to cart button.
	*/
	disable() {
		this.refs.addToCartButton.disabled = true;
	}
	/**
	* Enables the add to cart button.
	*/
	enable() {
		this.refs.addToCartButton.disabled = false;
	}
	/**
	* Handles the click event for the add to cart button.
	* @param {MouseEvent & {target: HTMLElement}} event - The click event.
	*/
	handleClick(event) {
		if (!this.closest("form")?.checkValidity()) return;
		const quantitySelector = this.closest("product-form-component")?.refs.quantitySelector;
		if (quantitySelector?.canAddToCart) {
			if (!quantitySelector.canAddToCart().canAdd) return;
		}
		if (this.refs.addToCartButton.dataset.puppet !== "true") {
			if (this.dataset.addToCartAnimation === "true" && !event.target.closest(".quick-add-modal")) this.#animateFlyToCart();
			this.animateAddToCart();
		}
	}
	#preloadImage = () => {
		const image = this.dataset.productVariantMedia;
		if (!image) return;
		preloadImage(image);
	};
	/**
	* Animates the fly to cart animation.
	*/
	#animateFlyToCart() {
		const { addToCartButton } = this.refs;
		const cartIcon = document.querySelector(".header-actions__cart-icon");
		const image = this.dataset.productVariantMedia;
		if (!cartIcon || !addToCartButton || !image) return;
		const flyToCartElement = document.createElement("fly-to-cart");
		let flyToCartClass = addToCartButton.classList.contains("quick-add__button") ? "fly-to-cart--quick" : "fly-to-cart--main";
		flyToCartElement.classList.add(flyToCartClass);
		flyToCartElement.style.setProperty("background-image", `url(${image})`);
		flyToCartElement.style.setProperty("--start-opacity", "0");
		flyToCartElement.source = addToCartButton;
		flyToCartElement.destination = cartIcon;
		document.body.appendChild(flyToCartElement);
	}
	/**
	* Animates the add to cart button.
	*/
	animateAddToCart = async function() {
		const { addToCartButton } = this.refs;
		if (!this.#resetTimeouts) this.#resetTimeouts = [];
		this.#resetTimeouts.forEach(
			/** @param {number} timeoutId */
			(timeoutId) => clearTimeout(timeoutId)
		);
		this.#resetTimeouts = [];
		if (addToCartButton.dataset.added !== "true") addToCartButton.dataset.added = "true";
		await yieldToMainThread();
		await onAnimationEnd(addToCartButton);
		const timeoutId = setTimeout(() => {
			addToCartButton.removeAttribute("data-added");
			const index = this.#resetTimeouts.indexOf(timeoutId);
			if (index > -1) this.#resetTimeouts.splice(index, 1);
		}, 800);
		this.#resetTimeouts.push(timeoutId);
	};
};
if (!customElements.get("add-to-cart-component")) customElements.define("add-to-cart-component", AddToCartComponent);
/**
* A custom element that manages a product form.
*
* @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
*
* @typedef {object} ProductFormRefs
* @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
* @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
* @property {HTMLButtonElement | undefined} addToCartButton - Direct add-to-cart button (Poelgeest PDP).
* @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
* @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
* @property {HTMLElement} liveRegion - The live region.
* @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
* @property {HTMLElement | undefined} quantityRules - The quantity rules element.
* @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
* @property {HTMLElement | undefined} volumePricing - The volume pricing component.
* @property {any | undefined} quantitySelector - The quantity selector component.
* @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
* @property {HTMLElement | undefined} quantityLabel - The quantity label element.
* @property {HTMLElement | undefined} pricePerItem - The price per item component.
*
* @typedef {object} QueuedAddToCartItem
* @property {number} quantity - The quantity captured when Add was clicked.
* @property {number} generation - The variant-change generation active when Add was clicked.
* @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
* @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
* @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
*
* @typedef {object} QuantityConstraints
* @property {string} min
* @property {string | null} max
* @property {string} step
* @property {string | null} cartQuantity
*
* @extends Component<ProductFormRefs>
*/
var ProductFormComponent = class extends Component {
	requiredRefs = ["variantId", "liveRegion"];
	#abortController = new AbortController();
	/** @type {number | undefined} */
	#timeout;
	/** @type {boolean} */
	#variantChangeInProgress = false;
	/** @type {number} */
	#variantChangeGeneration = 0;
	/**
	* Adds queued while a variant change is in flight. Each entry captures the selection state and
	* generation active when Add was clicked, then resolves that selection at drain time.
	* @type {QueuedAddToCartItem[]}
	*/
	#addToCartQueue = [];
	/**
	* The in-flight variant-change section fetch promise. The queue drain awaits this before
	* reading the resolved variant id.
	* @type {Promise<unknown> | null}
	*/
	#pendingVariantChange = null;
	connectedCallback() {
		super.connectedCallback();
		const { signal } = this.#abortController;
		this.closest(".shopify-section, dialog, product-card")?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
		document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	#getVariantIdInput() {
		return this.querySelector("input[name=\"id\"]")?.value;
	}
	async #refreshCart() {
		/** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
		const cartItemsComponent = document.querySelector("cart-items-component");
		if (cartItemsComponent) {
			await customElements.whenDefined("cart-items-component");
			return cartItemsComponent.fetchCartData();
		}
		return fetch(`${Theme.routes.cart_url}.json`, {
			headers: { Accept: "application/json" },
			credentials: "same-origin"
		}).then((response) => {
			if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
			return response.json();
		});
	}
	/**
	* Updates quantity selector with cart data for current variant
	* @param {Cart} cart - The cart object with items array
	*/
	#updateCartQuantity(cart) {
		const variantIdInput = this.#getVariantIdInput();
		if (!variantIdInput) return;
		const cartItem = cart.items.find(
			/** @param {any} item */
			(item) => item.variant_id.toString() === variantIdInput.toString()
		);
		const cartQty = cartItem ? cartItem.quantity : 0;
		const quantitySelector = this.querySelector("quantity-selector-component");
		if (quantitySelector?.setCartQuantity) quantitySelector.setCartQuantity(cartQty);
		this.#updateQuantityLabel(cartQty);
	}
	/**
	* Updates data-cart-quantity when cart is updated from elsewhere
	* @param {CartLinesUpdateEvent} event
	*/
	#onCartUpdate = async (event) => {
		if (!this.#getVariantIdInput()) return;
		event.promise?.then(({ detail }) => {
			if (detail?.sourceId === this.id || detail?.source === "product-form-component") return;
			if (detail?.items) this.#updateCartQuantity({ items: detail.items });
			else this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
		}).catch((error) => {
			if (error?.name !== "AbortError") console.warn("[product-form] Event promise rejected:", error);
		});
	};
	/** @param {Event} event */
	handleSubmit(event) {
		event.preventDefault();
		if (this.#variantChangeInProgress) {
			this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
			this.refs.addToCartButtonContainer?.animateAddToCart?.();
			return;
		}
		if (this.#tryAddWithPdpUpsells(event)) return;
		this.#processAddToCart(void 0, void 0, event);
	}
	/**
	* When PDP upsell checkboxes are selected, add main + upsells in one
	* /cart/add request so the main product is not duplicated.
	* @param {Event} [event]
	* @returns {boolean} true when the batch path handled the submit
	*/
	#tryAddWithPdpUpsells(event) {
		const upsellRoot = this.querySelector("vp-pdp-upsell");
		if (!upsellRoot) return false;
		const upsellIds = collectSelectedUpsellVariantIds(upsellRoot);
		if (!upsellIds.length) return false;
		const form = this.querySelector("form");
		if (!form) throw new Error("Product form element missing");
		const directButton = this.#getAddToCartButton();
		if (directButton?.disabled || directButton?.getAttribute("aria-busy") === "true") return true;
		const formData = new FormData(form);
		const mainId = String(formData.get("id") || "");
		const quantity = Number(formData.get("quantity")) || this.#getQuantity();
		if (!mainId) return false;
		const items = buildPdpUpsellCartItems(mainId, quantity, upsellIds).map((item) => ({
			variantId: String(item.id),
			quantity: item.quantity
		}));
		this.#processBatchAddToCart(items, cartAttributesFromFormData(formData), event);
		return true;
	}
	/** @returns {number} */
	#getQuantity() {
		return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
	}
	/** @returns {QueuedAddToCartItem} */
	#createQueuedAddToCartItem() {
		const picker = this.#getVariantPicker();
		const selectedOption = picker?.selectedOption;
		return {
			quantity: this.#getQuantity(),
			generation: this.#variantChangeGeneration,
			intendedVariantId: selectedOption?.dataset.variantId ?? null,
			pendingVariantChange: this.#pendingVariantChange,
			variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null
		};
	}
	/**
	* @param {string} [overrideVariantId]
	* @param {number} [overrideQuantity]
	* @param {Event} [event]
	*/
	#processAddToCart(overrideVariantId, overrideQuantity, event) {
		const { addToCartTextError } = this.refs;
		if (this.#timeout) clearTimeout(this.#timeout);
		const allAddToCartContainers = this.querySelectorAll("add-to-cart-component");
		if (!overrideVariantId) {
			if (Array.from(allAddToCartContainers).some((container) => container.refs.addToCartButton?.disabled)) return;
			const directButton = this.#getAddToCartButton();
			if (directButton?.disabled || directButton?.getAttribute("aria-busy") === "true") return;
		}
		const form = this.querySelector("form");
		if (!form) throw new Error("Product form element missing");
		if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
			const validation = this.refs.quantitySelector.canAddToCart();
			if (!validation.canAdd) {
				for (const container of allAddToCartContainers) container.disable();
				const errorMessage = (this.dataset.quantityErrorMax || "").replace("{{ maximum }}", validation.maxQuantity?.toString() || "");
				if (addToCartTextError) {
					addToCartTextError.classList.remove("hidden");
					const textNode = addToCartTextError.childNodes[2];
					if (textNode) textNode.textContent = errorMessage;
					else {
						const newTextNode = document.createTextNode(errorMessage);
						addToCartTextError.appendChild(newTextNode);
					}
					this.#setLiveRegionText(errorMessage);
					if (this.#timeout) clearTimeout(this.#timeout);
					this.#timeout = setTimeout(() => {
						if (!addToCartTextError) return;
						addToCartTextError.classList.add("hidden");
						this.#clearLiveRegionText();
					}, ERROR_MESSAGE_DISPLAY_DURATION);
				}
				setTimeout(() => {
					for (const container of allAddToCartContainers) container.enable();
				}, ERROR_BUTTON_REENABLE_DELAY);
				return;
			}
		}
		const formData = new FormData(form);
		if (overrideVariantId) formData.set("id", overrideVariantId);
		if (overrideQuantity !== void 0) formData.set("quantity", overrideQuantity.toString());
		const cartItemsComponents = document.querySelectorAll("cart-items-component");
		let cartItemComponentsSectionIds = [];
		cartItemsComponents.forEach((item) => {
			if (item instanceof HTMLElement && item.dataset.sectionId) cartItemComponentsSectionIds.push(item.dataset.sectionId);
			formData.append("sections", cartItemComponentsSectionIds.join(","));
		});
		const itemCount = Number(formData.get("quantity")) || Number(this.dataset.quantityDefault);
		const deferredEventPromise = CartLinesUpdateEvent.createPromise();
		this.dispatchEvent(new CartLinesUpdateEvent({
			action: "add",
			context: "product",
			lines: [{
				merchandiseId: formData.get("id"),
				quantity: itemCount
			}],
			promise: deferredEventPromise.promise
		}));
		this.#setAddToCartBusy(true);
		const fetchCfg = fetchConfig("javascript", { body: formData });
		fetch(Theme.routes.cart_add_url, {
			...fetchCfg,
			headers: {
				...fetchCfg.headers,
				Accept: "text/html"
			}
		}).then((response) => response.json()).then(async (response) => {
			if (response.status) {
				this.dispatchEvent(new CartErrorEvent({
					error: response.message || "Add to cart failed",
					code: "INVALID",
					detail: {
						description: response.description,
						errors: response.errors
					}
				}));
				this.#refreshCart().then((ajaxCart) => deferredEventPromise.resolve({
					cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
					detail: {
						didError: true,
						items: ajaxCart.items,
						source: "product-form-component",
						sourceId: this.id.toString(),
						itemCount,
						productId: this.dataset.productId
					}
				})).catch(deferredEventPromise.reject);
				if (!addToCartTextError) return;
				addToCartTextError.classList.remove("hidden");
				const textNode = addToCartTextError.childNodes[2];
				if (textNode) textNode.textContent = response.message;
				else {
					const newTextNode = document.createTextNode(response.message);
					addToCartTextError.appendChild(newTextNode);
				}
				this.#setLiveRegionText(response.message);
				this.#timeout = setTimeout(() => {
					if (!addToCartTextError) return;
					addToCartTextError.classList.add("hidden");
					this.#clearLiveRegionText();
				}, ERROR_MESSAGE_DISPLAY_DURATION);
				return;
			} else {
				const id = formData.get("id");
				if (addToCartTextError) {
					addToCartTextError.classList.add("hidden");
					addToCartTextError.removeAttribute("aria-live");
				}
				if (!id) throw new Error("Form ID is required");
				const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
				if (anyAddToCartButton) {
					const addedText = anyAddToCartButton.querySelector(".add-to-cart-text--added")?.textContent?.trim() || Theme.translations.added;
					this.#setLiveRegionText(addedText);
					setTimeout(() => {
						this.#clearLiveRegionText();
					}, SUCCESS_MESSAGE_DISPLAY_DURATION);
				}
				await this.#refreshCart().then((ajaxCart) => {
					deferredEventPromise.resolve({
						cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
						detail: {
							items: ajaxCart.items,
							source: "product-form-component",
							sourceId: this.id.toString(),
							itemCount,
							productId: this.dataset.productId,
							sections: response.sections,
							didError: false
						}
					});
					if (this.#getVariantIdInput()) this.#updateCartQuantity(ajaxCart);
					return ajaxCart;
				}).catch(deferredEventPromise.reject);
			}
		}).catch((error) => {
			console.error(error);
			deferredEventPromise.reject(error);
			this.dispatchEvent(new CartErrorEvent({
				error: error?.message || "Network error during add to cart",
				code: "SERVICE_UNAVAILABLE"
			}));
		}).finally(() => {
			this.#setAddToCartBusy(false);
			if (event) cartPerformance.measureFromEvent("add:user-action", event);
		});
	}
	/**
	* @param {Array<{variantId: string, quantity: number}>} items
	* @param {Record<string, string>} [attributes]
	* @param {Event} [event]
	*/
	#processBatchAddToCart(items, attributes, event) {
		if (items.length === 0) return;
		const { addToCartTextError } = this.refs;
		if (this.#timeout) clearTimeout(this.#timeout);
		const cartItemsComponents = document.querySelectorAll("cart-items-component");
		const cartItemComponentsSectionIds = [];
		for (const item of cartItemsComponents) if (item instanceof HTMLElement && item.dataset.sectionId) cartItemComponentsSectionIds.push(item.dataset.sectionId);
		const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
		const deferredEventPromise = CartLinesUpdateEvent.createPromise();
		this.dispatchEvent(new CartLinesUpdateEvent({
			action: "add",
			context: "product",
			lines: items.map((item) => ({
				merchandiseId: item.variantId,
				quantity: item.quantity
			})),
			promise: deferredEventPromise.promise
		}));
		/** @type {Record<string, unknown>} */
		const payload = {
			items: items.map((item) => ({
				id: Number(item.variantId),
				quantity: item.quantity
			})),
			sections: cartItemComponentsSectionIds.join(",")
		};
		if (attributes && Object.keys(attributes).length) payload.attributes = attributes;
		this.#setAddToCartBusy(true);
		fetch(Theme.routes.cart_add_url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json"
			},
			body: JSON.stringify(payload)
		}).then((response) => response.json()).then(async (response) => {
			if (response.status) {
				this.dispatchEvent(new CartErrorEvent({
					error: response.message || "Add to cart failed",
					code: "INVALID",
					detail: {
						description: response.description,
						errors: response.errors
					}
				}));
				this.#refreshCart().then((ajaxCart) => deferredEventPromise.resolve({
					cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
					detail: {
						didError: true,
						items: ajaxCart.items,
						source: "product-form-component",
						sourceId: this.id.toString(),
						itemCount: totalQuantity,
						productId: this.dataset.productId
					}
				})).catch(deferredEventPromise.reject);
				if (!addToCartTextError) return;
				addToCartTextError.classList.remove("hidden");
				const textNode = addToCartTextError.childNodes[2];
				if (textNode) textNode.textContent = response.message;
				else addToCartTextError.appendChild(document.createTextNode(response.message));
				this.#setLiveRegionText(response.message);
				this.#timeout = setTimeout(() => {
					addToCartTextError.classList.add("hidden");
					this.#clearLiveRegionText();
				}, ERROR_MESSAGE_DISPLAY_DURATION);
				return;
			}
			if (addToCartTextError) {
				addToCartTextError.classList.add("hidden");
				addToCartTextError.removeAttribute("aria-live");
			}
			const anyAddToCartButton = this.querySelectorAll("add-to-cart-component")[0]?.refs.addToCartButton;
			if (anyAddToCartButton) {
				const addedText = anyAddToCartButton.querySelector(".add-to-cart-text--added")?.textContent?.trim() || Theme.translations.added;
				this.#setLiveRegionText(addedText);
				setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
			}
			const cart = await this.#refreshCart();
			deferredEventPromise.resolve({
				cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
				detail: {
					items: cart.items,
					source: "product-form-component",
					sourceId: this.id.toString(),
					itemCount: totalQuantity,
					productId: this.dataset.productId,
					sections: response.sections,
					didError: false
				}
			});
			this.#updateCartQuantity(cart);
		}).catch((error) => {
			console.error(error);
			deferredEventPromise.reject(error);
			this.dispatchEvent(new CartErrorEvent({
				error: error?.message || "Network error during add to cart",
				code: "SERVICE_UNAVAILABLE"
			}));
		}).finally(() => {
			this.#setAddToCartBusy(false);
			if (event) cartPerformance.measureFromEvent("add:user-action", event);
		});
	}
	/**
	* Updates the quantity label with the current cart quantity
	* @param {number} cartQty - The quantity in cart
	*/
	#updateQuantityLabel(cartQty) {
		const quantityLabel = this.refs.quantityLabelCartCount;
		if (quantityLabel) {
			const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
			if (inCartText && inCartText[2]) quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
			quantityLabel.classList.toggle("hidden", cartQty === 0);
		}
	}
	/**
	* @param {*} text
	*/
	#setLiveRegionText(text) {
		const liveRegion = this.refs.liveRegion;
		liveRegion.textContent = text;
	}
	#clearLiveRegionText() {
		const liveRegion = this.refs.liveRegion;
		liveRegion.textContent = "";
	}
	/**
	* Morphs or removes/adds an element based on current and new element states
	* @param {Element | null | undefined} currentElement - The current element in the DOM
	* @param {Element | null | undefined} newElement - The new element from the server response
	* @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
	*/
	#morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
		if (currentElement && newElement) morph(currentElement, newElement);
		else if (currentElement && !newElement) currentElement.remove();
		else if (!currentElement && newElement && insertReferenceElement) insertReferenceElement.insertAdjacentElement("beforebegin", newElement.cloneNode(true));
	}
	/**
	* @param {ProductSelectEvent} event
	*/
	#onProductSelect = async (event) => {
		const sourceCard = event.target?.closest("product-card");
		if (sourceCard && !sourceCard.contains(this)) return;
		const generation = ++this.#variantChangeGeneration;
		this.#variantChangeInProgress = true;
		this.#pendingVariantChange = event.promise;
		try {
			const { detail } = await event.promise;
			if (!detail?.html) return;
			const { html, newProduct, productId, resource } = detail;
			if (newProduct) this.dataset.productId = newProduct.id;
			else if (productId && productId !== this.dataset.productId) return;
			const { variantId } = this.refs;
			variantId.value = resource?.id ?? "";
			const nextFormRoot = getProductFormRoot(html, this.dataset.productId);
			refreshProductAvailability(this, nextFormRoot, (current, next) => morph(current, next));
			syncProductAvailabilityFromVariant(this, resource);
			const currentAddToCartButton = this.#getAddToCartButton();
			const { acceleratedCheckoutButtonContainer } = this.refs;
			if (!currentAddToCartButton && !acceleratedCheckoutButtonContainer) return;
			this.#setAddToCartDisabled(resource == null || resource.available === false);
			const newAddToCartButton = nextFormRoot.querySelector("[ref=\"addToCartButton\"]");
			if (newAddToCartButton && currentAddToCartButton) morph(currentAddToCartButton, newAddToCartButton, { childrenOnly: false });
			if (acceleratedCheckoutButtonContainer) {
				if (resource == null || resource.available == false) acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
				else acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
			}
			if (resource) {
				const productVariantMedia = resource.featured_media?.preview_image?.src;
				if (productVariantMedia) (this.refs.addToCartButtonContainer ?? this.refs.addToCartButton)?.setAttribute("data-product-variant-media", productVariantMedia + "&width=100");
			}
			const { quantityRules, pricePerItem, quantitySelector, productFormButtons, quantityLabel, quantitySelectorWrapper } = this.refs;
			const newQuantityInput = html.querySelector("quantity-selector-component input[ref=\"quantityInput\"]");
			if (quantitySelector?.updateConstraints && newQuantityInput) {
				quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
				this.dataset.quantityDefault = newQuantityInput.min || "1";
			}
			const newQuantityRules = html.querySelector(".quantity-rules");
			const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;
			const newPricePerItem = html.querySelector("price-per-item");
			if ((isQuantityRulesChanging || !!pricePerItem !== !!newPricePerItem) && quantitySelector) {
				const currentQuantityValue = quantitySelector.getValue?.();
				const newProductFormButtons = html.querySelector(".product-form-buttons");
				if (productFormButtons && newProductFormButtons) {
					morph(productFormButtons, newProductFormButtons);
					const newQuantityInputElement = html.querySelector("quantity-selector-component input[ref=\"quantityInput\"]");
					if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
						this.refs.quantitySelector.setValue(currentQuantityValue);
						this.refs.quantitySelector.updateConstraints(newQuantityInputElement.min, newQuantityInputElement.max || null, newQuantityInputElement.step);
						this.dataset.quantityDefault = newQuantityInputElement.min || "1";
					}
				}
			} else {
				/** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
				const morphTargets = [
					[
						".quantity-label",
						quantityLabel,
						quantitySelector
					],
					[
						".quantity-rules",
						quantityRules,
						this.refs.productFormButtons
					],
					[
						"price-per-item",
						pricePerItem,
						quantitySelectorWrapper
					]
				];
				for (const [selector, currentElement, fallback] of morphTargets) this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
			}
			const currentVolumePricing = this.refs.volumePricing;
			const newVolumePricing = html.querySelector("volume-pricing");
			this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);
			if (!(quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing)) return;
			this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
		} finally {
			if (generation === this.#variantChangeGeneration) {
				this.#variantChangeInProgress = false;
				await this.#drainAddToCartQueue();
			}
		}
	};
	/**
	* Drains the add-to-cart queue accumulated while a variant change was in flight.
	*
	* Each queued add resolves against the selection and generation that were active when Add was
	* clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
	* variant id is never sent. The add-to-cart button is already disabled for unavailable
	* selections in #onProductSelect, so no further UI change is needed.
	*/
	async #drainAddToCartQueue() {
		if (this.#addToCartQueue.length === 0) return;
		const queuedItems = [...this.#addToCartQueue];
		this.#addToCartQueue = [];
		/** @type {Array<{variantId: string, quantity: number}>} */
		const resolvedItems = [];
		for (const item of queuedItems) {
			const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
			if (resolvedItem) resolvedItems.push(resolvedItem);
		}
		this.#processBatchAddToCart(resolvedItems);
	}
	/**
	* @param {QueuedAddToCartItem} item
	* @returns {Promise<{variantId: string, quantity: number} | null>}
	*/
	async #resolveQueuedAddToCartItem(item) {
		const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
		if (!variantId) return null;
		return {
			variantId,
			quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints)
		};
	}
	/**
	* @param {QueuedAddToCartItem} item
	* @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
	*/
	async #resolveQueuedVariant(item) {
		/** @type {string | null} */
		let resolvedVariantId = null;
		/** @type {boolean | undefined} */
		let available;
		/** @type {QuantityConstraints | null} */
		let quantityConstraints = null;
		if (item.pendingVariantChange) try {
			const result = await item.pendingVariantChange;
			const resource = result?.detail?.resource;
			quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
			if (resource === null) available = false;
			else if (resource) {
				resolvedVariantId = resource.id != null ? String(resource.id) : null;
				available = resource.available !== false;
			}
		} catch {
			const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
				variantId: null,
				available: false,
				quantityConstraints: null
			}));
			resolvedVariantId = resolvedVariant.variantId;
			available = resolvedVariant.available;
			quantityConstraints = resolvedVariant.quantityConstraints;
		}
		const isLatestGeneration = item.generation === this.#variantChangeGeneration;
		return {
			variantId: resolveVariantId({
				resolvedVariantId,
				intendedVariantId: item.intendedVariantId,
				hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
				available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : void 0)
			}),
			quantityConstraints
		};
	}
	/**
	* @param {number} quantity
	* @param {QuantityConstraints | null} quantityConstraints
	* @returns {number}
	*/
	#normalizeQueuedQuantity(quantity, quantityConstraints) {
		if (!quantityConstraints) return quantity;
		const min = parseIntOrDefault(quantityConstraints.min, 1);
		const max = parseIntOrDefault(quantityConstraints.max, null);
		const step = parseIntOrDefault(quantityConstraints.step, 1);
		const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
		const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);
		let normalizedQuantity = quantity;
		if ((quantity - min) % step !== 0) normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
		return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
	}
	/**
	* @param {Document | Element | null | undefined} html
	* @returns {QuantityConstraints | null}
	*/
	#getQuantityConstraintsFromHtml(html) {
		const quantityInput = html?.querySelector?.("quantity-selector-component input[ref=\"quantityInput\"]") ?? null;
		if (!quantityInput) return null;
		return {
			min: quantityInput.min,
			max: quantityInput.max || null,
			step: quantityInput.step,
			cartQuantity: quantityInput.getAttribute("data-cart-quantity")
		};
	}
	/**
	* Resolves a queued selection using the server-side section renderer when the original in-flight
	* request was aborted by a later variant selection.
	* @param {string | null} variantResolutionUrl
	* @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
	*/
	async #resolveVariantFromUrl(variantResolutionUrl) {
		if (!variantResolutionUrl) return {
			variantId: null,
			available: void 0,
			quantityConstraints: null
		};
		const response = await fetch(variantResolutionUrl, { credentials: "same-origin" });
		if (!response.ok) return {
			variantId: null,
			available: false,
			quantityConstraints: null
		};
		const html = new DOMParser().parseFromString(await response.text(), "text/html");
		const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
		const textContent = html.querySelector("variant-picker script[type=\"application/json\"]")?.textContent;
		if (!textContent) return {
			variantId: null,
			available: false,
			quantityConstraints
		};
		const resource = JSON.parse(textContent);
		if (!resource || typeof resource !== "object") return {
			variantId: null,
			available: false,
			quantityConstraints
		};
		return {
			variantId: resource.id != null ? String(resource.id) : null,
			available: resource.available !== false,
			quantityConstraints
		};
	}
	/**
	* @returns {import('@theme/variant-picker').default | null}
	*/
	#getVariantPicker() {
		const container = this.closest("product-card") ?? this.closest("dialog") ?? this.closest(".shopify-section");
		const pickers = Array.from(container?.querySelectorAll("variant-picker, swatches-variant-picker-component") ?? []);
		const productId = this.dataset.productId;
		if (productId) {
			const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
			if (matchingPicker) return matchingPicker;
		}
		return pickers.length === 1 ? pickers[0] ?? null : null;
	}
	/**
	* @returns {HTMLButtonElement | undefined}
	*/
	#getAddToCartButton() {
		const { addToCartButtonContainer, addToCartButton } = this.refs;
		if (addToCartButtonContainer?.refs.addToCartButton instanceof HTMLButtonElement) return addToCartButtonContainer.refs.addToCartButton;
		return addToCartButton instanceof HTMLButtonElement ? addToCartButton : void 0;
	}
	/**
	* In-flight loading on every ATC control (PDP vp-button + Horizon containers).
	* @param {boolean} busy
	*/
	#setAddToCartBusy(busy) {
		const containers = this.querySelectorAll("add-to-cart-component");
		for (const container of containers) {
			const button = container.refs.addToCartButton;
			if (!(button instanceof HTMLButtonElement)) continue;
			button.toggleAttribute("aria-busy", busy);
			if (busy) container.disable();
			else if (button.dataset.vpKeepDisabled !== "true") container.enable();
		}
		const button = this.#getAddToCartButton();
		if (button instanceof HTMLButtonElement && containers.length === 0) {
			button.toggleAttribute("aria-busy", busy);
			button.disabled = busy || button.dataset.vpKeepDisabled === "true";
		}
	}
	/**
	* @param {boolean} disabled
	*/
	#setAddToCartDisabled(disabled) {
		const { addToCartButtonContainer } = this.refs;
		const button = this.#getAddToCartButton();
		if (button) {
			if (disabled) button.dataset.vpKeepDisabled = "true";
			else delete button.dataset.vpKeepDisabled;
		}
		if (addToCartButtonContainer) {
			if (disabled) addToCartButtonContainer.disable();
			else if (button?.getAttribute("aria-busy") !== "true") addToCartButtonContainer.enable();
			return;
		}
		if (button && button.getAttribute("aria-busy") !== "true") button.disabled = disabled;
	}
	/**
	* Whether the current selection's add-to-cart button is disabled (unavailable selection).
	* @returns {boolean}
	*/
	#isAddToCartDisabled() {
		const containers = this.querySelectorAll("add-to-cart-component");
		if (containers.length > 0) return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
		return this.#getAddToCartButton()?.disabled === true;
	}
};
if (!customElements.get("product-form-component")) customElements.define("product-form-component", ProductFormComponent);
//#endregion
export { AddToCartComponent };
